/**
 * Shared auth redirect guard
 *
 * Prevents duplicate interactive redirects when multiple code paths
 * detect an expired/invalid session at the same time.
 */

const AUTH_REDIRECT_FLAG = 'auth_redirect_in_progress';
const AUTH_REDIRECT_COMPLETED_FLAG = 'auth_redirect_completed_at';
const REDIRECT_TTL_MS = 60000; // 1 minute safety window
const REDIRECT_COOLDOWN_MS = 10000; // 10 second cooldown after redirect completes

let inProgress = false;

export const isAuthRedirectInProgress = (): boolean => {
  if (inProgress) {
    return true;
  }

  const stored = sessionStorage.getItem(AUTH_REDIRECT_FLAG);
  if (!stored) {
    return false;
  }

  const startedAt = Number(stored);
  if (!startedAt || Number.isNaN(startedAt)) {
    sessionStorage.removeItem(AUTH_REDIRECT_FLAG);
    return false;
  }

  if (Date.now() - startedAt > REDIRECT_TTL_MS) {
    sessionStorage.removeItem(AUTH_REDIRECT_FLAG);
    return false;
  }

  return true;
};

export const markAuthRedirectInProgress = (): void => {
  inProgress = true;
  sessionStorage.setItem(AUTH_REDIRECT_FLAG, Date.now().toString());
};

export const clearAuthRedirectInProgress = (): void => {
  inProgress = false;
  sessionStorage.removeItem(AUTH_REDIRECT_FLAG);
  // Record when the redirect completed so we can enforce a cooldown
  // before allowing another redirect (prevents immediate re-redirect
  // when dashboard components fire API calls before tokens are ready)
  sessionStorage.setItem(AUTH_REDIRECT_COMPLETED_FLAG, Date.now().toString());
};

/**
 * Check if a redirect was recently completed (within cooldown period).
 * Used to prevent immediate re-redirect when navigating from callback to dashboard.
 */
export const isWithinRedirectCooldown = (): boolean => {
  const completedAt = sessionStorage.getItem(AUTH_REDIRECT_COMPLETED_FLAG);
  if (!completedAt) return false;
  const elapsed = Date.now() - Number(completedAt);
  return elapsed < REDIRECT_COOLDOWN_MS;
};
